import React, { useEffect, useMemo, useState } from "react";
import { AlertCircle, Bell, Gauge, Hash, Music2, Search, Send, X } from "lucide-react";
import { sendSingleNotification } from "../notifications/notificationApi";
import { fetchSongsV2, SongV2ListItem } from "../songV2/songV2Api";
import { adminAssignSongToUser } from "./userApi";
import styles from "./UserShared.module.css";

interface SendUserNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (message: string) => void;
  userId: string;
}

function parseDataPairs(input: string): Record<string, string> | undefined {
  const lines = input
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) return undefined;

  return lines.reduce<Record<string, string>>((result, line) => {
    const [key, ...value] = line.split("=");
    if (key.trim()) result[key.trim()] = value.join("=").trim();
    return result;
  }, {});
}

export function SendUserNotificationModal({
  isOpen,
  onClose,
  onComplete,
  userId,
}: SendUserNotificationModalProps) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [data, setData] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim() || !body.trim()) {
      setError("Bildirim başlığı ve mesajı zorunludur.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const summary = await sendSingleNotification({
        user_id: userId,
        title: title.trim(),
        body: body.trim(),
        data: parseDataPairs(data),
      });
      const hasNoActiveTokens = summary.reason === "no_active_tokens";
      if (summary.push_status && summary.push_status !== "sent" && !hasNoActiveTokens) {
        throw new Error(
          `Push gönderilmedi: ${summary.reason || summary.push_status || "aktif token bulunamadı"}.`
        );
      }
      onComplete(
        summary.push_status === "sent"
          ? `Bildirim gönderildi: ${summary.sent} başarılı, ${summary.failed} başarısız, ${summary.skipped} atlandı.`
          : hasNoActiveTokens
            ? "Bildirim isteği kabul edildi ancak kullanıcının aktif push cihazı bulunamadı."
            : "Bildirim isteği API tarafından başarıyla kabul edildi."
      );
      setTitle("");
      setBody("");
      setData("");
      onClose();
    } catch (err: any) {
      setError(err?.message || "Bildirim gönderilemedi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={styles.userActionOverlay}
      role="presentation"
      onMouseDown={onClose}
    >
      <section
        className={styles.userActionModal}
        role="dialog"
        aria-modal="true"
        aria-label="Tekil bildirim gönder"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className={styles.userActionModalHeader}>
          <span>
            <Bell size={20} />
          </span>
          <div>
            <h2>Tekil bildirim gönder</h2>
            <p>Bildirim bu kullanıcının kayıtlı push cihazlarına gönderilir.</p>
          </div>
          <button type="button" aria-label="Pencereyi kapat" onClick={onClose}>
            <X size={17} />
          </button>
        </header>

        <form className={styles.userActionForm} onSubmit={submit}>
          {error && (
            <div className={styles.userActionError}>
              <AlertCircle size={16} /> {error}
            </div>
          )}
          <label>
            <span>
              <Bell size={14} /> Başlık *
            </span>
            <input
              autoFocus
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={140}
              placeholder="Yeni içerik hazır"
              required
            />
          </label>
          <label>
            <span>
              <Send size={14} /> Mesaj *
            </span>
            <textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              maxLength={500}
              rows={4}
              placeholder="Kullanıcıya göndermek istediğiniz mesaj..."
              required
            />
          </label>
          <label>
            <span>Ek veri</span>
            <textarea
              value={data}
              onChange={(event) => setData(event.target.value)}
              rows={3}
              placeholder={"action=OPEN_SONG\nsongv2_id=..."}
            />
            <small>İsteğe bağlı. Her satıra bir `anahtar=değer` yazın.</small>
          </label>
          <div className={styles.notificationRecipient}>
            <Hash size={14} />
            <span>Alıcı</span>
            <strong>{userId}</strong>
          </div>
          <div className={styles.userActionFooter}>
            <button type="button" onClick={onClose} disabled={loading}>
              İptal
            </button>
            <button
              type="submit"
              disabled={loading || !title.trim() || !body.trim()}
            >
              <Send size={16} />
              {loading ? "Gönderiliyor" : "Bildirimi gönder"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

interface AssignUserSongModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (message: string) => void;
  userId: string;
  assignedSongIds: string[];
}

export function AssignUserSongModal({
  isOpen,
  onClose,
  onComplete,
  userId,
  assignedSongIds,
}: AssignUserSongModalProps) {
  const [songs, setSongs] = useState<SongV2ListItem[]>([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError("");
    fetchSongsV2({ limit: 100 })
      .then((result) => setSongs(result.data))
      .catch((err: any) =>
        setError(err?.message || "Şarkı kataloğu yüklenemedi.")
      )
      .finally(() => setLoading(false));
  }, [isOpen]);

  const visibleSongs = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("tr-TR");
    if (!query) return songs;
    return songs.filter((song) =>
      [song.title, song.artist, song.songv2_id].some((value) =>
        value.toLocaleLowerCase("tr-TR").includes(query)
      )
    );
  }, [search, songs]);

  if (!isOpen) return null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedId) {
      setError("Atanacak bir şarkı seçin.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const result = await adminAssignSongToUser(userId, selectedId);
      onComplete(result.message);
      setSelectedId("");
      setSearch("");
      onClose();
    } catch (err: any) {
      setError(err?.message || "Şarkı kullanıcıya atanamadı.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.userActionOverlay} role="presentation" onMouseDown={onClose}>
      <section className={`${styles.userActionModal} ${styles.songAssignModal}`} role="dialog" aria-modal="true" aria-label="Kullanıcıya V2 şarkı ata" onMouseDown={(event) => event.stopPropagation()}>
        <header className={styles.userActionModalHeader}>
          <span><Music2 size={20} /></span>
          <div><h2>V2 şarkı ata</h2><p>Katalogdan bir şarkı seçerek kullanıcıya ekleyin.</p></div>
          <button type="button" onClick={onClose}><X size={17} /></button>
        </header>
        <form className={styles.userActionForm} onSubmit={submit}>
          {error && <div className={styles.userActionError}><AlertCircle size={16} /> {error}</div>}
          <label className={styles.songPickerSearch}>
            <span><Search size={14} /> Katalogda ara</span>
            <input autoFocus value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Şarkı, sanatçı veya songv2_id..." />
          </label>
          <div className={styles.songPickerList}>
            {loading ? (
              <p>Şarkılar yükleniyor...</p>
            ) : visibleSongs.length === 0 ? (
              <p>Eşleşen şarkı bulunamadı.</p>
            ) : (
              visibleSongs.map((song) => {
                const assigned = assignedSongIds.includes(song.songv2_id);
                return (
                  <button
                    type="button"
                    key={song.songv2_id}
                    className={selectedId === song.songv2_id ? styles.songPickerSelected : undefined}
                    disabled={assigned}
                    onClick={() => setSelectedId(song.songv2_id)}
                  >
                    <span><Music2 size={16} /></span>
                    <div><strong>{song.title}</strong><small>{song.artist} · {song.songv2_id}</small></div>
                    <i><Gauge size={12} /> {song.bpm}</i>
                    {assigned && <em>Atanmış</em>}
                  </button>
                );
              })
            )}
          </div>
          <div className={styles.userActionFooter}>
            <button type="button" onClick={onClose} disabled={saving}>İptal</button>
            <button type="submit" disabled={saving || !selectedId}><Music2 size={16} />{saving ? "Atanıyor" : "Şarkıyı ata"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}
